import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authService } from '@/features/auth/services/authService';

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  user: 'User',
};

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'bg-purple-100 text-purple-700 border-purple-200',
  admin: 'bg-blue-100 text-blue-700 border-blue-200',
  user: 'bg-slate-100 text-slate-600 border-slate-200',
};

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { appUser, getAccessToken, refreshProfile } = useAuth();

  const [name, setName] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Populate form from appUser on mount / when appUser refreshes
  useEffect(() => {
    if (!appUser) return;
    setName(appUser.name ?? '');
  }, [appUser]);

  if (!appUser) return null;

  const hasChanges = name.trim() !== (appUser.name ?? '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token');
      await authService.updateProfile(token, { name: name.trim() });
      await refreshProfile();
      setSuccessMessage('Profile updated successfully.');
    } catch {
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const rol = appUser.rol ?? 'user';
  const roleLabel = ROLE_LABELS[rol] ?? rol;
  const roleColor = ROLE_COLORS[rol] ?? ROLE_COLORS.user;

  const divisionDisplay = (appUser.division ?? []).join(', ') || '—';
  const languageDisplay = appUser.language ?? '—';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 md:px-6">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            aria-label="Back to chat"
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold text-slate-800">My Profile</h1>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-4 py-8 md:px-6">
        <div className="max-w-2xl mx-auto">
          {/* User card */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6 flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-xl font-semibold shrink-0">
              {(appUser.name ?? appUser.email ?? '?')[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-800 truncate">{appUser.name || '—'}</p>
              <p className="text-sm text-slate-500 truncate">{appUser.email}</p>
              <span className={`inline-flex items-center mt-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border ${roleColor}`}>
                {roleLabel}
              </span>
            </div>
          </div>

          {/* Edit form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col gap-5">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Edit information</h2>

            {/* Name — editable */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="profile-name" className="text-sm font-medium text-slate-700">
                Name
              </label>
              <input
                id="profile-name"
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setSuccessMessage(null); }}
                placeholder="Your full name"
                disabled={isSaving}
                className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                           focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                           disabled:bg-slate-50 disabled:text-slate-400"
              />
            </div>

            {/* Email — read-only */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="profile-email" className="text-sm font-medium text-slate-700">
                Email <span className="text-slate-400 font-normal">(cannot be changed)</span>
              </label>
              <input
                id="profile-email"
                type="email"
                value={appUser.email}
                disabled
                className="rounded-lg border border-slate-200 px-3 py-2 text-base
                           bg-slate-50 text-slate-400 cursor-not-allowed"
              />
            </div>

            {/* Country — read-only */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="profile-country" className="text-sm font-medium text-slate-700">
                Country
              </label>
              <input
                id="profile-country"
                type="text"
                value={appUser.country ?? '—'}
                disabled
                className="rounded-lg border border-slate-200 px-3 py-2 text-base
                           bg-slate-50 text-slate-400 cursor-not-allowed"
              />
            </div>

            {/* Division — read-only */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="profile-division" className="text-sm font-medium text-slate-700">
                Division
              </label>
              <input
                id="profile-division"
                type="text"
                value={divisionDisplay}
                disabled
                className="rounded-lg border border-slate-200 px-3 py-2 text-base
                           bg-slate-50 text-slate-400 cursor-not-allowed"
              />
            </div>

            {/* Language — read-only */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="profile-language" className="text-sm font-medium text-slate-700">
                Language
              </label>
              <input
                id="profile-language"
                type="text"
                value={languageDisplay}
                disabled
                className="rounded-lg border border-slate-200 px-3 py-2 text-base
                           bg-slate-50 text-slate-400 cursor-not-allowed"
              />
            </div>

            {/* Success */}
            {successMessage && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
                <CheckCircle className="h-4 w-4 shrink-0" />
                <span>{successMessage}</span>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end pt-1">
              <Button type="submit" disabled={isSaving || !hasChanges}>
                {isSaving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};
